import { ATTACHMENT_LIMITS } from "./enums";
import { formatSizeMB } from "./attachment_validator";

/**
 * Browser-side video compression via ffmpeg.wasm.
 *
 * The library is neither imported nor an npm dependency. Two reasons:
 * webpack 4 (CRA 4) cannot parse it — its UMD build uses ES2022 private class
 * fields — and the 30.7 MB core has no business in this repo or its lockfile.
 * The UMD bundle is served from public/ (see scripts/copy-ffmpeg.js), loaded
 * with a <script> tag on first use, and used through the global it defines.
 *
 * Because it is not installed, its types are not resolvable either, so the
 * small surface we call is declared below. Bump this if the pinned version in
 * scripts/copy-ffmpeg.js changes.
 */

type FFmpegProgressEvent = { progress: number; time: number };

interface FFmpegLike {
  load(config: { coreURL: string; wasmURL: string }): Promise<boolean>;
  writeFile(path: string, data: Uint8Array): Promise<boolean>;
  readFile(path: string): Promise<Uint8Array | string>;
  deleteFile(path: string): Promise<boolean>;
  exec(args: string[]): Promise<number>;
  on(event: "progress", callback: (event: FFmpegProgressEvent) => void): void;
  off(event: "progress", callback: (event: FFmpegProgressEvent) => void): void;
  terminate(): void;
}

// Keep in sync with DIST_DIR in scripts/copy-ffmpeg.js.
const FFMPEG_DIST_PATH = "/ffmpeg/0.12.15-core0.12.10";

/**
 * 540p rather than 720p. Measured on a 20 s 1080p30 clip: 540p came out 49%
 * smaller *and* 30% faster than 720p at the same CRF (5.5 s / 3.41 MB versus
 * 7.8 s / 6.7 MB). Still easily legible for complaint evidence.
 */
const TARGET_HEIGHT = 540;
const AUDIO_KBPS = 96;
/**
 * Frames, not bytes, drive encode time, so cap the rate: phone clips are often
 * 30 or 60 fps and complaint evidence reads fine at 24.
 */
const TARGET_FPS = 24;
/** Leaves room for container overhead when aiming at a size budget. */
const SIZE_SAFETY = 0.92;
/**
 * Floor for the size-budget pass. At 100 kbps video plus 48 kbps mono audio, a
 * 30 MB budget stretches to roughly 27 minutes of footage — so in practice any
 * clip small enough to pass the 100 MB input ceiling fits by lowering quality
 * alone, and nobody is ever asked to shorten a video.
 */
const MIN_VIDEO_KBPS = 100;

export const VIDEO_MAX_MB = ATTACHMENT_LIMITS.video.max_size_mb;
export const VIDEO_MAX_INPUT_MB = ATTACHMENT_LIMITS.video.max_input_size_mb ?? 100;

export type CompressionStage = "loading" | "compressing";

export interface CompressionProgress {
  stage: CompressionStage;
  /** 0-100 within the current stage. */
  percent: number;
}

export interface CompressionResult {
  file: File;
  compressed: boolean;
  originalSize: number;
  /** Set when we fell back to the original, for logging/messaging. */
  fallbackReason?: string;
}

declare global {
  interface Window {
    FFmpegWASM?: { FFmpeg: new () => FFmpegLike };
  }
}

let scriptPromise: Promise<void> | undefined;
let ffmpegPromise: Promise<FFmpegLike> | undefined;

const loadScript = () => {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const src = `${FFMPEG_DIST_PATH}/ffmpeg.js`;
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`,
    );

    if (existing && window.FFmpegWASM) return resolve();

    const script = existing ?? document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(
        new Error(
          "Could not load the video compressor. Please refresh and try again.",
        ),
      );

    if (!existing) document.body.appendChild(script);
  }).catch((error) => {
    // Let a later attempt retry instead of caching the failure forever.
    scriptPromise = undefined;
    throw error;
  });

  return scriptPromise;
};

const absolute = (file: string) =>
  new URL(`${FFMPEG_DIST_PATH}/${file}`, window.location.href).href;

/**
 * Only videos that would be rejected for size are worth compressing.
 *
 * Measured: single-threaded wasm runs at roughly 2.5x realtime on a fast Mac and
 * slower on field laptops, so compressing a long clip costs minutes — and the
 * first one also pays a 30.7 MB core download. A clip already within the cap
 * gains nothing from that, so it uploads untouched and never loads the core.
 */
export const shouldCompressVideo = (file: File) =>
  file.size > VIDEO_MAX_MB * 1024 * 1024;

export const isVideoCompressionSupported = () =>
  typeof WebAssembly !== "undefined" && typeof Worker !== "undefined";

const getFFmpeg = (onProgress?: (percent: number) => void) => {
  if (ffmpegPromise) return ffmpegPromise;

  ffmpegPromise = (async () => {
    await loadScript();

    const factory = window.FFmpegWASM;
    if (!factory) throw new Error("Video compressor failed to initialise.");

    const ffmpeg = new factory.FFmpeg();

    // Deliberately no classWorkerURL: passing it makes the UMD build create a
    // *module* worker, which has no importScripts, so the core can never load
    // ("Cannot find module <coreURL>"). Omitting it uses a classic worker and
    // resolves 814.ffmpeg.js from this script's own directory — which is why
    // scripts/copy-ffmpeg.js puts both files side by side.
    await ffmpeg.load({
      coreURL: absolute("ffmpeg-core.js"),
      wasmURL: absolute("ffmpeg-core.wasm"),
    });

    onProgress?.(100);
    return ffmpeg;
  })().catch((error) => {
    ffmpegPromise = undefined;
    throw error;
  });

  return ffmpegPromise;
};

/** Duration in seconds, read from a throwaway <video> — cheaper than ffprobe. */
const readDuration = (file: File) =>
  new Promise<number | undefined>((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");

    const done = (value: number | undefined) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };

    video.preload = "metadata";
    video.onloadedmetadata = () =>
      done(Number.isFinite(video.duration) ? video.duration : undefined);
    video.onerror = () => done(undefined);
    video.src = url;
  });

/**
 * Only ever reduces *quality* — resolution, frame rate and bitrate. Duration is
 * never touched: the clip that comes out is as long as the one that went in.
 *
 * Measured on a 20 s 1080p30 clip in Chrome: `ultrafast` + an fps cap took a
 * 15.7 s encode down to 7.7 s, and 540p took it to 5.5 s. Encode cost tracks
 * pixels processed (duration x fps x frame size), *not* output size — CRF 30 to
 * 33 cut the file 36% with no time change — so a smaller target is free, and a
 * smaller frame is faster. Do not add `-x264-params`/`-tune fastdecode`: both
 * measured *slower*.
 */
const videoFilter = (height: number) =>
  `scale='min(${Math.round((height * 16) / 9)},iw)':'min(${height},ih)':force_original_aspect_ratio=decrease:force_divisible_by=2,fps=${TARGET_FPS}`;

const qualityArgs = () => [
  "-vf",
  videoFilter(TARGET_HEIGHT),
  "-c:v",
  "libx264",
  "-preset",
  "ultrafast",
  "-crf",
  "30",
  "-c:a",
  "aac",
  "-b:a",
  `${AUDIO_KBPS}k`,
  "-movflags",
  "+faststart",
];

/** Audio also gives way when the budget is tight, before quality drops further. */
const audioArgs = (videoKbps: number) =>
  videoKbps < 200
    ? ["-c:a", "aac", "-ac", "1", "-b:a", "48k"]
    : videoKbps < 400
      ? ["-c:a", "aac", "-ac", "1", "-b:a", "64k"]
      : ["-c:a", "aac", "-b:a", `${AUDIO_KBPS}k`];

export const audioKbpsFor = (videoKbps: number) =>
  videoKbps < 200 ? 48 : videoKbps < 400 ? 64 : AUDIO_KBPS;

const bitrateArgs = (videoKbps: number, height: number) => [
  "-vf",
  videoFilter(height),
  "-c:v",
  "libx264",
  "-preset",
  "ultrafast",
  "-b:v",
  `${videoKbps}k`,
  "-maxrate",
  `${Math.round(videoKbps * 1.2)}k`,
  "-bufsize",
  `${Math.round(videoKbps * 2)}k`,
  ...audioArgs(videoKbps),
  "-movflags",
  "+faststart",
];

/**
 * Compresses an oversized video to H.264/AAC MP4 at 720p.
 *
 * Videos within the cap are returned untouched (see shouldCompressVideo), as is
 * anything that fails to compress — the caller then reports the size problem,
 * rather than a transcode error becoming a dead end.
 */
export const compressVideo = async (
  file: File,
  {
    onProgress,
    signal,
  }: {
    onProgress?: (progress: CompressionProgress) => void;
    signal?: AbortSignal;
  } = {},
): Promise<CompressionResult> => {
  const original: CompressionResult = {
    file,
    compressed: false,
    originalSize: file.size,
  };

  // Belt and braces: callers check this too, so they can skip showing progress
  // UI for a video that needs no work.
  if (!shouldCompressVideo(file)) return original;

  if (!isVideoCompressionSupported())
    return { ...original, fallbackReason: "browser cannot run the compressor" };

  const input = "input";
  const output = "output.mp4";
  let ffmpeg: FFmpegLike | undefined;

  try {
    onProgress?.({ stage: "loading", percent: 0 });
    ffmpeg = await getFFmpeg((percent) =>
      onProgress?.({ stage: "loading", percent }),
    );

    if (signal?.aborted) throw new Error("cancelled");

    const onTick = ({ progress }: { progress: number }) =>
      onProgress?.({
        stage: "compressing",
        percent: Math.min(100, Math.max(0, progress * 100)),
      });

    ffmpeg.on("progress", onTick);

    const abort = () => ffmpeg?.terminate();
    signal?.addEventListener("abort", abort);

    try {
      await ffmpeg.writeFile(input, new Uint8Array(await file.arrayBuffer()));
      await ffmpeg.exec(["-i", input, ...qualityArgs(), output]);

      let data = (await ffmpeg.readFile(output)) as Uint8Array;

      // Still over the cap: spend a second pass on a size budget derived from
      // the duration. A longer clip simply gets a lower bitrate and, if that
      // bitrate is too low to look sane at 720p, a smaller frame — the clip is
      // never shortened to make it fit.
      if (data.byteLength > VIDEO_MAX_MB * 1024 * 1024) {
        const duration = await readDuration(file);

        if (duration && duration > 0) {
          const totalKbps = (VIDEO_MAX_MB * 8192 * SIZE_SAFETY) / duration;
          // Solve for video bitrate knowing audio shrinks alongside it.
          const provisional = Math.floor(totalKbps - AUDIO_KBPS);
          const videoKbps = Math.max(
            MIN_VIDEO_KBPS,
            Math.floor(totalKbps - audioKbpsFor(provisional)),
          );
          const height =
            videoKbps < 300 ? 360 : videoKbps < 600 ? 480 : TARGET_HEIGHT;

          await ffmpeg.deleteFile(output);
          await ffmpeg.exec([
            "-i",
            input,
            ...bitrateArgs(videoKbps, height),
            output,
          ]);
          data = (await ffmpeg.readFile(output)) as Uint8Array;
        }
      }

      const compressed = new File(
        [new Blob([data], { type: "video/mp4" })],
        file.name.replace(/\.[^.]+$/, "") + ".mp4",
        { type: "video/mp4" },
      );

      // Re-encoding an already-compressed clip can make it bigger.
      if (compressed.size >= file.size)
        return {
          ...original,
          fallbackReason: `compressed copy was larger (${formatSizeMB(
            compressed.size,
          )} vs ${formatSizeMB(file.size)})`,
        };

      return {
        file: compressed,
        compressed: true,
        originalSize: file.size,
      };
    } finally {
      signal?.removeEventListener("abort", abort);
      ffmpeg.off("progress", onTick);

      // terminate() kills the worker, so only clean up if it is still alive.
      if (!signal?.aborted) {
        await ffmpeg.deleteFile(input).catch(() => undefined);
        await ffmpeg.deleteFile(output).catch(() => undefined);
      }
    }
  } catch (error: any) {
    // A terminated worker cannot be reused.
    if (signal?.aborted || error?.message === "cancelled")
      ffmpegPromise = undefined;

    return {
      ...original,
      fallbackReason: error?.message ?? "compression failed",
    };
  }
};
